import clsx from 'clsx';
import type * as React from 'react';

import type { ButtonProps } from '../Button';
import { Button, ButtonSkeleton } from '../Button';

export type PillKind = 'pill' | 'pill-upgrade' | 'pill-secondary';

/**
 * A pill component, used to display a single item in a compact way. It can be used to display tags, categories, or any other type of metadata.
 */
export function Pill(props: Omit<ButtonProps, 'kind'> & { kind?: PillKind }) {
    const { kind = 'pill', ...rest } = props;
    return <Button {...rest} kind={kind} size="small" />;
}

/**
 * A stack of pills, used to display a list of items in a compact way.
 */
export function PillStack(props: React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) {
    const { children, className, ...rest } = props;

    return (
        <div className={clsx('flex flex-row flex-wrap gap-2', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * A skeleton loader for a stack of pills, used to indicate loading state while fetching data for the pills.
 */
export function PillStackSkeleton() {
    return (
        <PillStack>
            <PillSkeleton />
            <PillSkeleton />
            <PillSkeleton />
        </PillStack>
    );
}

/**
 * A skeleton loader for a pill, used to indicate loading state while fetching data for a pill.
 */
function PillSkeleton() {
    return <ButtonSkeleton size="small" rounded label="Pill label" />;
}
